import { Box, CssBaseline } from "@mui/material";
import { useEffect, useState } from "react";
import NavBar from "./NavBar";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import SoftwareDashboard from "../../dashboards/SoftwareDashboard";
import CompanyDashboard from "../../dashboards/CompanyDashboard";
import CompanyDetails from "../../details/CompanyDetails";
import SubscriptionDashboard from "../../dashboards/SubscriptionDashboard";
import UserDashboard from "../../dashboards/UserDashboard";
import LoginPage from "./LoginPage";
import { jwtDecode } from "jwt-decode";
import agent from "../../../api/agent";
import PageNotFound from "../../errors/PageNotFound";
import UserProfile from "../../details/UserProfile";
import LicenseByUser from "../../details/LicenseByUser";
import { showToast, ToastMessage } from "./ToastMessage";
import LogDashboard from "../../dashboards/LogDashboard";
import DBBackupDashboard from "../../dashboards/DBBackupDashboard";

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userRole, setUserRole] = useState<string>("");
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [userId, setUserId] = useState<number>(0);
  const [companyId, setCompanyId] = useState<number>(0);

  useEffect(() => {
    // get token from localStorage
    const token = localStorage.getItem("token");
    if (token !== null) {
      // decode the jwt and save the role of login user
      const decodedToken: any = jwtDecode(token);

      // Save the email user logged in for future use
      setUserId(decodedToken.sub);

      const expirationTime = decodedToken.exp * 1000;
      const currentTime = Date.now();

      // to check the token expiration
      if (expirationTime < currentTime) {
        localStorage.removeItem("token");
        alert("Token has expired. Please log in again.");
        setIsLoggedIn(false);
        window.location.replace("/Login");
        return;
      }

      if (location.pathname === "/login") {
        // replace with the home page routing
        window.location.replace("/softwares");
        return;
      }
      const fetchData = async () => {
        // get and store the role of the user logged
        const userResponse = await agent.User.getUserDetail(
          decodedToken.sub,
          token,
        );
        setUserRole(userResponse.role);
        setCompanyId(userResponse.companyId);
        setIsLoggedIn(true);
      };
      fetchData();
    } else {
      setIsLoggedIn(false);
      if (location.pathname !== "/login") {
        window.location.replace("/login");
        return;
      }
    }
  }, [location.pathname]);

  // Toggle sidebar function
  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Function to log out the user
  function handleLogout() {
    showToast("Successfully log out");
    localStorage.removeItem("token");
    setIsLoggedIn(false);
    // window.location.replace("/login");
    navigate("/login");
    return;
  }

  return (
    <>
      {/* For the toast message */}
      <ToastMessage />

      {/* To check login state only show the nav bar */}
      {isLoggedIn ? (
        <Box
          sx={{
            display: "flex",
            position: "relative",
            backgroundColor: "#f5f5f5",
            minHeight: "100vh",
          }}
        >
          <CssBaseline />
          <NavBar
            sidebarOpen={sidebarOpen}
            handleSidebarToggle={handleSidebarToggle}
            userRole={userRole}
            handleLogout={handleLogout}
            companyId={companyId}
          />

          {/* Main Content Area */}
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              p: 3,
              mt: 8,
              transition: "filter 0.3s ease-in-out",
              filter: sidebarOpen ? "blur(2px)" : "none",
              backgroundColor: "#f5f5f5", // Blur effect when sidebar opens
            }}
          >
            <Box
              sx={{
                backgroundColor: "white", // White background for the content container
                borderRadius: 2, // Optional: rounded corners
                boxShadow: 1, // Optional: subtle shadow
                p: 3, // Padding inside the white container
                minHeight: "calc(100vh - 64px - 24px)", // Adjust height considering header and padding
              }}
            >
              <Routes>
                {/* routes and pages that Only open for super admin level user  */}
                {userRole === "Super Admin" && (
                  <>
                    <Route path="/softwares" element={<SoftwareDashboard />} />
                    <Route path="/company" element={<CompanyDashboard />} />
                    <Route
                      path="/subscription"
                      element={<SubscriptionDashboard />}
                    />
                    <Route path="/user" element={<UserDashboard />} />
                    <Route
                      path="/company/details/:companyId"
                      element={<CompanyDetails />}
                    />
                    <Route path="/logs" element={<LogDashboard />} />
                    <Route path="/database-backups" element={<DBBackupDashboard />} />

                  </>
                )}
                {/* routes and pages that Only open for admin level user */}
                {userRole === "Admin" && (
                  <>
                    {/* To show the software dialog, but without create, edit and delete */}
                    <Route
                      path="/company/details/:companyId"
                      element={<CompanyDetails />}
                    />
                  </>
                )}

                {/* routes and pages that Only open for normal level user */}
                {userRole === "User" && (
                  <>
                    <Route path="/subscription" element={<LicenseByUser />} />
                  </>
                )}

                {/* Routes and pages that allow all level user to go */}
                <>
                  <Route
                    path="profile"
                    element={
                      <UserProfile userId={userId} logout={handleLogout} />
                    }
                  />
                  <Route path="*" element={<PageNotFound />} />
                </>
              </Routes>
            </Box>
          </Box>

          {/* Dimming effect when sidebar is open */}
          {sidebarOpen && (
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.5)", // Dim effect
                zIndex: 999,
                transition: "opacity 0.3s ease-in-out",
              }}
              onClick={handleSidebarToggle} // Clicking outside closes sidebar
            />
          )}
        </Box>
      ) : (
        <Routes>
          <Route path="/login" element={<LoginPage />} />
        </Routes>
      )}
    </>
  );
}

export default App;
